import { useEffect, type ComponentProps } from 'react';
import { useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider, OrdersProvider, FeatureFlagsProvider } from '@smarty/shared';
import { AppProvider } from '@/lib/polaris';
import { PolarisLink } from '@/components/polaris/PolarisLink';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { getTenantFromDomain } from '@/lib/tenant-resolver';
import { polarisI18n } from '@/components/polaris/polarisI18n';
import { initDB } from '@/lib/db';
import { startAutoSync, stopAutoSync, setupConnectionListeners } from '@/lib/sync';
import { AUTO_SYNC_INTERVAL } from '@/config/cache.config';
import { getHostname, isAppDomain } from '@/lib/domain';
import { router } from '@/routes/router';
import { PanelRumProbe } from '@/components/analytics/PanelRumProbe';
import { LegacyDomainGate } from '@/components/LegacyDomainGate';
import { devLog } from '@/lib/utils';
import { useSentryIdentify } from '@/hooks/useSentryIdentify';




/** Sincroniza identidad del usuario autenticado con Sentry para trazabilidad de errores. */
function SentryIdentify() {
  useSentryIdentify();
  return null;
}


const App = () => {
  return (
    <ErrorBoundary>
      <AppProvider
        i18n={polarisI18n as ComponentProps<typeof AppProvider>['i18n']}
        linkComponent={PolarisLink as ComponentProps<typeof AppProvider>['linkComponent']}
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
            <SentryIdentify />
            <OrdersProvider>
              <Toaster />
              <Sonner />
              {/* Audio para WebRTC (Telnyx): debe existir siempre en el DOM para que la llamada no se corte al cambiar de ruta */}
              <audio
                id="telnyx-remote-audio"
                autoPlay
                playsInline
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                aria-hidden
              />
              {/* Router de datos (ADR-0012, paso 2). El árbol de rutas y los providers que antes
                  vivían entre el router y las rutas se mudaron a `src/routes/router.tsx`: con
                  `RouterProvider` no se puede meter nada en el medio, así que eso es ahora la capa
                  raíz del árbol. Sin ningún `loader` todavía, a propósito. */}
              <RouterProvider
                router={router}
                // 🪤 Este flag es de `RouterProvider`, no de `createBrowserRouter`: envuelve las
                // actualizaciones de navegación en `startTransition`, que es lo que evita que una
                // ruta lazy congele la interfaz mientras baja su chunk. Estaba en `BrowserRouter`;
                // si se perdía en la mudanza, la regresión sería sutil y sin error.
                future={{ v7_startTransition: true }}
              />
            </OrdersProvider>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </AppProvider>
    </ErrorBoundary>
  );
};

export default App;
